import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { jsonLdScript, webPage } from '@/lib/schema'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowRight } from 'lucide-react'
import { BrowserCheckCta } from '@/components/browser-check-cta'

export const Route = createFileRoute('/_site/tools/downtime-cost-calculator')({
  component: DowntimeCostCalculatorPage,
  head: () => ({
    meta: [
      { title: 'Downtime Cost Calculator — Revenue at Risk' },
      { name: 'description', content: 'Calculate downtime cost: enter revenue and downtime for per-minute, per-incident and annual impact. Industry benchmarks.' },
      { property: 'og:title', content: 'Downtime Cost Calculator — How Much Does Downtime Cost You?' },
      { property: 'og:description', content: 'Calculate how much downtime costs your business per minute, per incident, and per year.' },
      { property: 'og:image', content: 'https://uptrack.app/og/tools-downtime-cost-calculator.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/tools/downtime-cost-calculator' },
    ],
    scripts: [
      jsonLdScript(webPage({
        name: 'Downtime Cost Calculator',
        description: 'Calculate the financial impact of downtime on your business based on revenue and incident frequency.',
        url: 'https://uptrack.app/tools/downtime-cost-calculator',
      })),
    ],
  }),
})

const benchmarks = [
  { company: 'Amazon', costPerMin: '$220,000' },
  { company: 'Facebook', costPerMin: '$110,000' },
  { company: 'Google', costPerMin: '$66,000' },
  { company: 'Average enterprise', costPerMin: '$9,000' },
  { company: 'Average SMB', costPerMin: '$427' },
]

function currency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${n.toFixed(2)}`
}

function DowntimeCostCalculatorPage() {
  const [revenue, setRevenue] = useState(50000)
  const [downtimeMinutes, setDowntimeMinutes] = useState(60)
  const [incidents, setIncidents] = useState(3)

  const revenuePerMinute = revenue / (30.44 * 24 * 60)
  const costPerMinute = revenuePerMinute
  const avgIncidentLength = incidents > 0 ? downtimeMinutes / incidents : 0
  const costPerIncident = costPerMinute * avgIncidentLength
  const monthlyCost = costPerMinute * downtimeMinutes
  const annualCost = monthlyCost * 12

  return (
    <>
      <>
        <section className="container mx-auto px-4 pb-8 pt-12 md:pt-16">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Downtime Cost Calculator</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">How much does downtime actually cost your business? Enter your numbers to find out.</p>
          </div>
        </section>

        <section className="pb-12 md:pb-16">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="rounded-lg border border-border bg-card p-5 md:p-6">
              <div className="mb-6 grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="revenue" className="mb-2 block text-sm font-medium">Monthly revenue ($)</Label>
                  <Input id="revenue" type="number" min="0" step="1000" value={revenue} onChange={(e) => setRevenue(parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label htmlFor="downtime" className="mb-2 block text-sm font-medium">Downtime/month (min)</Label>
                  <Input id="downtime" type="number" min="0" step="1" value={downtimeMinutes} onChange={(e) => setDowntimeMinutes(parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label htmlFor="incidents" className="mb-2 block text-sm font-medium">Incidents/month</Label>
                  <Input id="incidents" type="number" min="0" step="1" value={incidents} onChange={(e) => setIncidents(parseFloat(e.target.value) || 0)} />
                </div>
              </div>

              <div className="mb-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Cost per minute of downtime</p>
                  <p className="mt-1 num text-lg font-semibold text-foreground">{currency(costPerMinute)}</p>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Cost per incident</p>
                  <p className="mt-1 num text-lg font-semibold text-foreground">{currency(costPerIncident)}</p>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Monthly cost of downtime</p>
                  <p className="mt-1 text-lg font-semibold text-status-down">{currency(monthlyCost)}</p>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Annual cost of downtime</p>
                  <p className="mt-1 text-2xl font-semibold text-status-down">{currency(annualCost)}</p>
                </div>
              </div>

              {annualCost > 0 && (
                <div className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
                  Reducing downtime by just 50% would save you <span className="font-semibold text-primary">{currency(annualCost / 2)}/year</span>.
                  Faster detection means faster resolution.
                </div>
              )}
            </div>

            <div className="mt-8 rounded-lg border border-border bg-card p-5 md:p-6">
              <h2 className="mb-4 text-lg font-semibold">Industry benchmarks: cost per minute of downtime</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-3 text-left font-medium text-muted-foreground">Company / tier</th>
                    <th className="pb-3 text-right font-medium text-muted-foreground">Cost per minute</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarks.map((b) => (
                    <tr key={b.company} className="border-b border-border last:border-0">
                      <td className="py-2 font-medium">{b.company}</td>
                      <td className="py-2 text-right num text-muted-foreground">{b.costPerMin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-muted-foreground">
                Sources: Gartner, ITIC, AWS re:Invent. Figures represent estimated revenue loss only.
              </p>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-card">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Monitor your uptime with Uptrack</h2>
            <p className="mb-6 mt-3 max-w-xl text-body">
              Pro includes 50 monitors — detect downtime in seconds, not minutes.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        <section className="container mx-auto max-w-3xl px-4 pb-16">
          <BrowserCheckCta />
        </section>
      </>
      </>
  )
}
